import React, { Dispatch, useEffect } from 'react';
import IFund from '../../interfaces/fund/IFund';
import FFundPerformanceDetailsTable from '../../features/fund/components/FFundPerformanceDetailsTable';
import { Box, Typography } from '@mui/material';
import CGraphAssetsTrend from '../../components/fund/CGraphAssetsTrend';
import CAppInfoTooltip from '../../../../app/components/CAppInfoTooltip';
import langInvestFundPerformancesListedAssetsLang from '../../langs/langInvestFundPerformancesListedAssetsLang';
import { useTranslation } from 'react-i18next';
import useInvestFundPerformancesSG from '../../hooks/fund/useInvestFundPerformancesSG';
import { CSummaryCardProps } from '../../../../app/components/Card/SummaryCard/CSummaryCard';
import { CSummaryCardsInline } from '../../../../app/components/Card/SummaryCard/CSummaryCardInline';
import AppFormatAmount from '../../../../app/features/format/AppFormatAmount';
import AppFormatPercent from '../../../../app/features/format/AppFormatPercent';
import AppLoaderPage from '../../../../app/features/loader/page/AppLoaderPage';
import { formatLatestNonNullValueDate } from '@/app/utils/utils';

type VInvestFundPerformanceListedAssetsProps = {
	fund: IFund;
	setDataUpdatedOn: Dispatch<Date>;
};

const VInvestFundPerformanceListedAssets = (props: VInvestFundPerformanceListedAssetsProps) => {
	const { fund, setDataUpdatedOn } = props;

	const { isFetching, performances } = useInvestFundPerformancesSG(fund.id);
	const { t } = useTranslation();

	useEffect(() => {
		performances?.capital?.dataUpdatedOn && setDataUpdatedOn(performances.capital.dataUpdatedOn);
	}, [performances, setDataUpdatedOn]);

	const updatedOnDate = formatLatestNonNullValueDate(performances?.capital?.data, 'share');

	const summaryCardsList: CSummaryCardProps[] = isFetching
		? []
		: [
				{
					title: t(langInvestFundPerformancesListedAssetsLang.net_unit_assets.tag, {
						date: updatedOnDate,
					}),
					content: (
						<AppFormatAmount
							value={performances?.lastKpi?.assetTotalValue}
							currency={fund?.currency}
							customFloatDigitLimit={3}
						/>
					),
				},
				{
					title: t(langInvestFundPerformancesListedAssetsLang.unit_net_asset_value.tag, {
						date: updatedOnDate,
					}),
					content: (
						<AppFormatAmount
							value={performances?.lastKpi?.navPerShare}
							currency={fund?.currency}
							customFloatDigitLimit={3}
							disableIntegerRounding
						/>
					),
				},
				{
					title: t(langInvestFundPerformancesListedAssetsLang.ytd_percent_change.tag, {
						date: updatedOnDate,
					}),
					content: (
						<AppFormatPercent
							value={performances?.annualPerformances?.[0]?.value}
							textIfNull="n.c."
							displayPlus
						/>
					),
				},
		  ];

	return isFetching || !performances ? (
		<AppLoaderPage />
	) : (
		<>
			<CGraphAssetsTrend
				title={performances.capital?.title}
				fund={fund}
				dataset={performances.capital?.data ?? []}
			/>
			<CSummaryCardsInline list={summaryCardsList} />
			{/* TODO(technical debt): Find viable alternative in backend for removing 'MH CROISSANCE HAUT-DE-FRANCE - PART A' fund performances */}
			{fund.name !== 'MH CROISSANCE HAUT-DE-FRANCE - PART A' && (
				<>
					<Typography variant="h3" component="h3" marginTop="1em">
						{t(langInvestFundPerformancesListedAssetsLang.performance_details.tag)}
						<CAppInfoTooltip
							title={t(langInvestFundPerformancesListedAssetsLang.performance_details_tooltip.tag)}
						/>
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
						<FFundPerformanceDetailsTable
							performances={performances.cumulativePerformances ?? []}
							title={t(langInvestFundPerformancesListedAssetsLang.cumulative_performance_percent.tag)}
							isTranslatable
						/>
						<FFundPerformanceDetailsTable
							performances={performances.annualizedPerformances ?? []}
							title={t(langInvestFundPerformancesListedAssetsLang.annualized_performance_percent.tag)}
							isTranslatable
						/>
						<FFundPerformanceDetailsTable
							performances={performances.annualPerformances ?? []}
							title={t(langInvestFundPerformancesListedAssetsLang.annual_performance_percent.tag)}
						/>
					</Box>
				</>
			)}
		</>
	);
};

export default VInvestFundPerformanceListedAssets;
